import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Stack,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Avatar,
} from '@mui/joy';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../../components/Layout/MainLayout';
import api from '../../utils/api';

const Dashboard = () => {
  const { user, isAdmin } = useAuth();

  const [stats, setStats] = useState({
    devices: { total: 0, online: 0, offline: 0 },
    content: { total: 0, active: 0, pending: 0 },
    schedules: { total: 0, running: 0, paused: 0 },
    users: { total: 0, active: 0, admins: 0 },
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    const intervals = [
      { label: 'year', secs: 31536000 },
      { label: 'month', secs: 2592000 },
      { label: 'week', secs: 604800 },
      { label: 'day', secs: 86400 },
      { label: 'hour', secs: 3600 },
      { label: 'minute', secs: 60 },
    ];
    for (const it of intervals) {
      const count = Math.floor(seconds / it.secs);
      if (count >= 1) return `${count} ${it.label}${count > 1 ? 's' : ''} ago`;
    }
    return 'just now';
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const requests = [
          api.get('/api/devices/stats/overview'),
          api.get('/api/content/stats/overview'),
          api.get('/api/schedules/stats/overview'),
          api.get('/api/analytics/dashboard'),
        ];

        if (isAdmin()) {
          requests.push(api.get('/api/users'));
        }

        const responses = await Promise.allSettled(requests);

        const devicesRes = responses[0].status === 'fulfilled' ? responses[0].value?.data : null;
        const contentRes = responses[1].status === 'fulfilled' ? responses[1].value?.data : null;
        const schedulesRes = responses[2].status === 'fulfilled' ? responses[2].value?.data : null;
        const analyticsRes = responses[3].status === 'fulfilled' ? responses[3].value?.data : null;
        const usersRes = isAdmin() ? (responses[4]?.status === 'fulfilled' ? responses[4].value?.data : null) : null;

        const deviceOverview = devicesRes?.statistics?.overview;
        const contentOverview = contentRes?.statistics?.overview;
        const scheduleOverview = schedulesRes?.statistics?.overview;

        const nextStats = { ...stats };

        if (deviceOverview) {
          nextStats.devices.total = Number(deviceOverview.total_devices || 0);
          nextStats.devices.online = Number(deviceOverview.online_devices || 0);
          nextStats.devices.offline = Number(deviceOverview.offline_devices || 0);
        }

        if (contentOverview) {
          nextStats.content.total = Number(contentOverview.total_content || 0);
          nextStats.content.active = Number(contentOverview.active_content || 0);
          nextStats.content.pending = 0; // Not provided by API
        }

        if (scheduleOverview) {
          const total = Number(scheduleOverview.total_schedules || 0);
          const active = Number(scheduleOverview.active_schedules || 0);
          nextStats.schedules.total = total;
          nextStats.schedules.running = active;
          nextStats.schedules.paused = Math.max(0, total - active);
        }

        if (usersRes && usersRes.users && Array.isArray(usersRes.users)) {
          const totalUsers = usersRes.users.length;
          const activeUsers = usersRes.users.filter(u => u.status === 'active').length;
          const adminUsers = usersRes.users.filter(u => u.role === 'admin').length;
          nextStats.users = { total: totalUsers, active: activeUsers, admins: adminUsers };
        }

        if (analyticsRes?.dashboard?.recent_activity) {
          const mapped = analyticsRes.dashboard.recent_activity.map((a, idx) => ({
            id: idx,
            type: a.type_name || 'content_display',
            user: a.device_name || 'System',
            action: `${a.content_name || 'Content'} on ${a.device_name || 'device'}${a.location ? ' @ ' + a.location : ''}`,
            time: a.displayed_at ? formatTimeAgo(a.displayed_at) : '',
            avatar: a.type_name === 'video' ? '🎬' : a.type_name === 'image' ? '🖼️' : '📄',
          }));
          if (isMounted) setRecentActivity(mapped);
        }

        if (isMounted) setStats(nextStats);
      } catch (err) {
        console.error('Failed to load dashboard data', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [isAdmin]);

  const quickActions = [
    {
      label: 'Upload Content',
      icon: '📤',
      path: '/content',
      permission: 'manage_content',
      description: 'Add new media files'
    },
    {
      label: 'Add Device',
      icon: '📺',
      path: '/devices',
      permission: 'manage_devices',
      description: 'Register new display'
    },
    {
      label: 'Create Schedule',
      icon: '📅',
      path: '/scheduling',
      permission: 'manage_schedules',
      description: 'Plan content display'
    },
    {
      label: 'View Analytics',
      icon: '📊',
      path: '/analytics',
      permission: 'view_analytics',
      description: 'System performance'
    },
  ];

  return (
    <MainLayout currentPage="dashboard">
      <Box>
        {/* Welcome Section */}
        <Box sx={{ mb: 4 }}>
          <Typography level="h2" sx={{ fontWeight: 600, mb: 1 }}>
            Welcome back, {user?.name}! 👋
          </Typography>
          <Typography level="body-lg" sx={{ color: 'neutral.600' }}>
            Here's what's happening with your digital signage system today.
          </Typography>
        </Box>

        {/* Stats Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography level="title-lg">Devices</Typography>
                  <Box sx={{ fontSize: '1.5rem' }}>📺</Box>
                </Box>
                <Typography level="h2" sx={{ color: 'primary.500', mb: 1 }}>
                  {stats.devices.total}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip size="sm" variant="soft" color="success">
                    {stats.devices.online} online
                  </Chip>
                  {stats.devices.offline > 0 && (
                    <Chip size="sm" variant="soft" color="danger">
                      {stats.devices.offline} offline
                    </Chip>
                  )}
                </Stack>
                <LinearProgress
                  determinate
                  value={(stats.devices.online / stats.devices.total) * 100}
                  sx={{ height: 4 }}
                  color="success"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography level="title-lg">Content</Typography>
                  <Box sx={{ fontSize: '1.5rem' }}>🎬</Box>
                </Box>
                <Typography level="h2" sx={{ color: 'primary.500', mb: 1 }}>
                  {stats.content.total}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip size="sm" variant="soft" color="success">
                    {stats.content.active} active
                  </Chip>
                  {stats.content.pending > 0 && (
                    <Chip size="sm" variant="soft" color="warning">
                      {stats.content.pending} pending
                    </Chip>
                  )}
                </Stack>
                <LinearProgress
                  determinate
                  value={(stats.content.active / stats.content.total) * 100}
                  sx={{ height: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography level="title-lg">Schedules</Typography>
                  <Box sx={{ fontSize: '1.5rem' }}>📅</Box>
                </Box>
                <Typography level="h2" sx={{ color: 'primary.500', mb: 1 }}>
                  {stats.schedules.total}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip size="sm" variant="soft" color="success">
                    {stats.schedules.running} running
                  </Chip>
                  {stats.schedules.paused > 0 && (
                    <Chip size="sm" variant="soft" color="neutral">
                      {stats.schedules.paused} paused
                    </Chip>
                  )}
                </Stack>
                <LinearProgress
                  determinate
                  value={(stats.schedules.running / stats.schedules.total) * 100}
                  sx={{ height: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>

          {isAdmin() && (
            <Grid xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography level="title-lg">Users</Typography>
                    <Box sx={{ fontSize: '1.5rem' }}>👥</Box>
                  </Box>
                  <Typography level="h2" sx={{ color: 'primary.500', mb: 1 }}>
                    {stats.users.total}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip size="sm" variant="soft" color="success">
                      {stats.users.active} active
                    </Chip>
                    <Chip size="sm" variant="soft" color="warning">
                      {stats.users.admins} admins
                    </Chip>
                  </Stack>
                  <LinearProgress
                    determinate
                    value={(stats.users.active / stats.users.total) * 100}
                    sx={{ height: 4 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        <Grid container spacing={3}>
          {/* Quick Actions */}
          <Grid xs={12} md={8}>
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-lg" sx={{ mb: 3 }}>
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  {quickActions
                    .filter(action => !action.permission || user?.permissions?.includes(action.permission))
                    .map((action) => (
                      <Grid key={action.label} xs={12} sm={6}>
                        <Card
                          variant="soft"
                          sx={{
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 'md',
                            }
                          }}
                          onClick={() => window.location.href = action.path}
                        >
                          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ fontSize: '2rem' }}>{action.icon}</Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                                {action.label}
                              </Typography>
                              <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                                {action.description}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Activity */}
          <Grid xs={12} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography level="title-lg" sx={{ mb: 3 }}>
                  Recent Activity
                </Typography>
                <List sx={{ p: 0 }}>
                  {recentActivity.map((activity) => (
                    <ListItem key={activity.id} sx={{ px: 0, py: 1 }}>
                      <ListItemDecorator>
                        <Avatar size="sm" sx={{ fontSize: '0.875rem' }}>
                          {activity.avatar}
                        </Avatar>
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="body-sm" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {activity.action}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          by {activity.user} • {activity.time}
                        </Typography>
                      </ListItemContent>
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="plain"
                  size="sm"
                  sx={{ mt: 2, width: '100%' }}
                  onClick={() => window.location.href = '/analytics'}
                >
                  View All Activity
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* System Health Alert */}
        <Card
          variant="soft"
          color="success"
          sx={{ mt: 4 }}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ fontSize: '1.5rem' }}>✅</Box>
            <Box sx={{ flex: 1 }}>
              <Typography level="title-sm" sx={{ fontWeight: 600, mb: 0.5 }}>
                System Status: All Good
              </Typography>
              <Typography level="body-sm">
                All systems are operational. {stats.devices.online} devices online,
                {stats.content.active} content items active, and {stats.schedules.running} schedules running.
              </Typography>
            </Box>
            <Button
              variant="soft"
              size="sm"
              onClick={() => window.location.href = '/analytics'}
            >
              View Details
            </Button>
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
};

export default Dashboard;